import { useState } from 'react';
import { Tag, Eye, ThumbsUp, Star, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { markArticleHelpful } from '../../api/knowledge';

// Helper function to calculate reading time
const getReadingTime = (text) => {
  if (!text) return '1 min read';
  const words = text.split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
};

const ArticleCard = ({ 
  article, 
  onClick, 
  onFavoriteToggle, 
  onHelpfulToggle,
  isFavorite = false,
  isTrending = false,
  viewMode = 'list' 
}) => {
  const [helpfulSubmitting, setHelpfulSubmitting] = useState(false);

  if (!article) return null;

  const readingTime = getReadingTime(article.content || article.short_description);

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (onFavoriteToggle) {
      onFavoriteToggle();
    }
  };

  const handleShareClick = async (e) => {
    e.stopPropagation();
    try {
      const link = `${window.location.origin}/knowledge/articles/${article.id}`;
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleHelpfulClick = async (e) => {
    e.stopPropagation();
    if (helpfulSubmitting) return;
    setHelpfulSubmitting(true);
    try {
      await markArticleHelpful(article.id);
      toast.success('Thank you for your feedback!');
      if (onHelpfulToggle) {
        onHelpfulToggle();
      }
    } catch (error) {
      console.error('Failed to mark as helpful:', error);
      toast.error(error.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setHelpfulSubmitting(false);
    }
  };

  if (viewMode === 'grid') {
    return (
      <div
        className={`group relative bg-white dark:bg-slate-800 rounded-xl border p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col h-full ${
          isTrending 
            ? 'border-indigo-300 dark:border-indigo-500/50 shadow-lg shadow-indigo-500/10' 
            : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500'
        }`}
        onClick={onClick}
        role="button"
        tabIndex={0}
      >
        {/* Hover left stripe */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 to-blue-600 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-bottom z-10" />
        
        {/* FIX: Moved Trending Badge to Top-Left Ribbon to avoid overlapping buttons */}
        {isTrending && (
          <div className="absolute top-0 left-0 bg-gradient-to-br from-indigo-600 to-violet-600 text-white text-[10px] font-bold px-3 py-1 rounded-br-lg shadow-sm z-20">
            🔥 Trending
          </div>
        )}

        {/* Content Top */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 pr-2 pt-2"> {/* Added pt-2 to clear ribbon if text is long */}
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {article.title}
            </h3>
          </div>
          
          {/* Action Buttons (Top Right) */}
          <div className="flex items-center gap-1 flex-shrink-0 -mr-2 -mt-2">
            <button
              type="button"
              onClick={handleShareClick}
              className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
              title="Share Link"
            >
              <Share2 className="w-4 h-4" />
            </button>
            {onFavoriteToggle && (
              <button
                type="button"
                onClick={handleFavoriteClick}
                className={`p-2 rounded-lg transition-all ${
                  isFavorite 
                    ? 'text-yellow-500 bg-yellow-500/10' 
                    : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 dark:text-gray-500 dark:hover:text-yellow-400'
                }`}
                title={isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="flex-1 mb-6">
          {(article.short_description || article.content) && (
            <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3 leading-relaxed">
              {article.short_description || article.content}
            </p>
          )}
        </div>

        {/* Footer Metrics */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-auto pt-4 border-t border-gray-100 dark:border-slate-700/50">
          {article.category_name && (
            <span className="inline-flex items-center space-x-1 px-2.5 py-1 bg-gray-100 dark:bg-slate-700/50 text-gray-700 dark:text-gray-300 rounded-md font-medium">
              <Tag className="w-3 h-3" />
              <span>{article.category_name}</span>
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {readingTime}
          </span>
          <div className="flex-1"></div>
          <span className="flex items-center gap-1" title="Views">
            <Eye className="w-3 h-3" />
            {article.views || 0}
          </span>
          <button
            type="button"
            onClick={handleHelpfulClick}
            disabled={helpfulSubmitting}
            className="flex items-center gap-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            title="Mark as helpful"
          >
            <ThumbsUp className="w-3 h-3" />
            {article.helpful_count || 0}
          </button>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div
      className="group relative bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5 hover:border-indigo-300 dark:hover:border-indigo-500 hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden"
      onClick={onClick}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 to-blue-600 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-bottom" />
      
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isTrending && (
              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 text-[10px] font-bold rounded uppercase">
                Trending
              </span>
            )}
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate">
              {article.title}
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
            {article.short_description || article.content}
          </p>
          
          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            {article.category_name && (
              <span className="flex items-center gap-1 font-medium text-gray-700 dark:text-gray-300">
                <Tag className="w-3 h-3" />
                {article.category_name}
              </span>
            )}
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {readingTime}</span>
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {article.views || 0}</span>
            <button
              type="button"
              onClick={handleHelpfulClick}
              disabled={helpfulSubmitting}
              className="flex items-center gap-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              title="Mark as helpful"
            >
              <ThumbsUp className="w-3 h-3" />
              {article.helpful_count || 0}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={handleShareClick}
            className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
            title="Share Link"
          >
            <Share2 className="w-4 h-4" />
          </button>
          {onFavoriteToggle && (
            <button
              onClick={handleFavoriteClick}
              className={`p-2 rounded-lg transition-all ${
                isFavorite ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'
              }`}
            >
              <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleCard;
